// client/esweb/components/address-autocomplete2.tsx
"use client"

import React, {
    forwardRef,
    useState,
    useRef,
    useImperativeHandle,
    useEffect,
    useMemo,
    useCallback,
    type FocusEvent,
    type KeyboardEvent,
} from "react"
import { Input } from "@root/server/client/esweb/components/ui/input"
import { Button } from "@root/server/client/esweb/components/ui/button"
import { Spinner } from "@root/server/client/esweb/components/ui/spinner"
import { MapPin, X } from "lucide-react"
import { cn } from "@root/server/client/esweb/lib/utils"

export interface AddressItem {
    title: string
    subtitle: string
    location: {
        lon: number
        lat: number
        municipality: string
        address: string
    }
}

type BaseInputProps = Omit<
    React.ComponentPropsWithoutRef<typeof Input>,
    "value" | "defaultValue" | "onChange" | "ref"
>

interface AddressAutocompleteProps extends BaseInputProps {
    /** Controlled address state */
    value?: AddressItem | null
    /** Initial address when uncontrolled */
    defaultValue?: AddressItem | null
    /** Called with the selected address (or null when cleared) */
    onAddressChange?: (value: AddressItem | null) => void
    /** Called whenever the raw text changes */
    onInputValueChange?: (value: string) => void
    /** Called after clear */
    onClear?: () => void
    /** Name for the hidden input used by uncontrolled libraries */
    name?: string
    /** Optional flag to render error styling */
    hasError?: boolean
    /** Optional custom class for the dropdown container */
    dropdownClassName?: string
}

function renderAddress(address: AddressItem | null | undefined) {
    return address?.location.address ?? ""
}

export const AddressAutocomplete2 = forwardRef<HTMLInputElement, AddressAutocompleteProps>(
    (
        {
            value,
            defaultValue = null,
            onAddressChange,
            onInputValueChange,
            onBlur,
            onFocus,
            onClear,
            name,
            placeholder,
            className,
            hasError,
            dropdownClassName,
            disabled,
            id,
            ...rest
        },
        forwardedRef,
    ) => {
        const isControlled = value !== undefined
        const [internalAddress, setInternalAddress] = useState<AddressItem | null>(defaultValue)
        const [inputValue, setInputValue] = useState(() => renderAddress(isControlled ? value : internalAddress))
        const [suggestions, setSuggestions] = useState<AddressItem[]>([])
        const [loading, setLoading] = useState(false)
        const [isOpen, setIsOpen] = useState(false)
        const [highlightIndex, setHighlightIndex] = useState(-1)
        const [hasTyped, setHasTyped] = useState(false)

        const inputRef = useRef<HTMLInputElement>(null)
        const dropdownRef = useRef<HTMLDivElement>(null)
        const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
        const blurRef = useRef<ReturnType<typeof setTimeout>>(null)
        const controllerRef = useRef<AbortController>(null)

        const selectedAddress = isControlled ? value ?? null : internalAddress

        const closeDropdown = useCallback(() => {
            setIsOpen(false)
            setHighlightIndex(-1)
        }, [])

        const commitSelection = useCallback(
            (next: AddressItem | null) => {
                if (!isControlled) {
                    setInternalAddress(next)
                }
                onAddressChange?.(next)
                closeDropdown()
            },
            [closeDropdown, isControlled, onAddressChange],
        )

        const clearSelection = useCallback(() => {
            commitSelection(null)
            setInputValue("")
            setHasTyped(false)
            onInputValueChange?.("")
            onClear?.()
        }, [commitSelection, onClear, onInputValueChange])

        useImperativeHandle(forwardedRef, () => inputRef.current!, [])

        useEffect(() => {
            return () => {
                if (debounceRef.current) clearTimeout(debounceRef.current)
                if (blurRef.current) clearTimeout(blurRef.current)
                controllerRef.current?.abort()
            }
        }, [])

        useEffect(() => {
            if (isControlled) {
                setInputValue(renderAddress(value))
            }
        }, [isControlled, value])

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (
                    dropdownRef.current &&
                    !dropdownRef.current.contains(event.target as Node) &&
                    inputRef.current &&
                    !inputRef.current.contains(event.target as Node)
                ) {
                    closeDropdown()
                }
            }

            document.addEventListener("mousedown", handleClickOutside)
            return () => document.removeEventListener("mousedown", handleClickOutside)
        }, [closeDropdown])

        const fetchSuggestions = useCallback(async (query: string) => {
            if (!query || query.length < 2) {
                setSuggestions([])
                closeDropdown()
                return
            }

            if (!hasTyped) return

            try {
                controllerRef.current?.abort()
                controllerRef.current = new AbortController()
                setLoading(true)
                const response = await fetch(`/web/address/autocomplete.json?q=${encodeURIComponent(query)}`, {
                    signal: controllerRef.current.signal,
                })
                const data = (await response.json()) as { items?: AddressItem[] }
                const newItems = data.items ?? []
                setSuggestions(newItems)
                setIsOpen(newItems.length > 0 && document.activeElement === inputRef.current)
                if(inputRef.current) {
                    inputRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }

                setHighlightIndex(newItems.length > 0 ? 0 : -1)
            } catch (error) {
                if ((error as DOMException).name !== "AbortError") {
                    console.error("Error fetching address suggestions", error)
                    setSuggestions([])
                    closeDropdown()
                }
            } finally {
                setLoading(false)
            }
        }, [closeDropdown, hasTyped])

        const handleInputChange = useCallback(
            (nextValue: string) => {
                setInputValue(nextValue)
                setHasTyped(true)
                onInputValueChange?.(nextValue)
                commitSelection(null)

                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
                debounceRef.current = setTimeout(() => fetchSuggestions(nextValue), 400)
            },
            [commitSelection, fetchSuggestions, onInputValueChange],
        )

        const handleSelect = useCallback(
            (address: AddressItem) => {
                commitSelection(address)
                const label = renderAddress(address)
                setInputValue(label)
                onInputValueChange?.(label)
                setHasTyped(false)
            },
            [commitSelection, onInputValueChange],
        )

        const handleBlurInternal = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                blurRef.current = setTimeout(() => {
                    if (document.activeElement === inputRef.current) return
                    if (!selectedAddress && inputValue) {
                        clearSelection()
                    }
                    closeDropdown()
                    onBlur?.(event)
                }, 150)
            },
            [clearSelection, closeDropdown, inputValue, onBlur, selectedAddress],
        )

        const handleFocusInternal = useCallback(
            (event: FocusEvent<HTMLInputElement>) => {
                if (suggestions.length > 0) {
                    setIsOpen(true)
                }
                onFocus?.(event)
            },
            [onFocus, suggestions.length],
        )

        const handleKeyDown = useCallback(
            (event: KeyboardEvent<HTMLInputElement>) => {
                if (!isOpen || suggestions.length === 0) return

                if (event.key === "ArrowDown") {
                    event.preventDefault()
                    setHighlightIndex((index) => Math.min(index + 1, suggestions.length - 1))
                } else if (event.key === "ArrowUp") {
                    event.preventDefault()
                    setHighlightIndex((index) => Math.max(index - 1, 0))
                } else if (event.key === "Enter") {
                    event.preventDefault()
                    if (highlightIndex >= 0) {
                        handleSelect(suggestions[highlightIndex])
                    }
                } else if (event.key === "Escape") {
                    event.preventDefault()
                    closeDropdown()
                }
            },
            [closeDropdown, handleSelect, highlightIndex, isOpen, suggestions],
        )

        const hiddenValue = useMemo(
            () => (selectedAddress ? JSON.stringify(selectedAddress) : ""),
            [selectedAddress],
        )

        return (
            <div className="relative">
                {name ? (
                    <input type="hidden" name={name} value={hiddenValue} readOnly />
                ) : null}

                <Input
                    {...rest}
                    id={id}
                    ref={inputRef}
                    value={inputValue}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                    className={cn(hasError && "border-destructive focus-visible:ring-destructive", inputValue && "pr-10", 'bg-white', className)}
                    onChange={(event) => handleInputChange(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocusInternal}
                    onBlur={handleBlurInternal}
                />

                {inputValue ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                        tabIndex={-1}
                        onMouseDown={(event) => {
                            event.preventDefault()
                            clearSelection()
                            inputRef.current?.focus()
                        }}
                    >
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </Button>
                ) : null}

                {isOpen ? (
                    <div
                        ref={dropdownRef}
                        role="listbox"
                        className={cn(
                            "absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover shadow-md",
                            dropdownClassName,
                        )}
                    >
                        {loading ? (
                            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                                <Spinner className="h-4 w-4" />
                                Načítání…
                            </div>
                        ) : suggestions.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-muted-foreground">Žádné výsledky</div>
                        ) : (
                            suggestions.map((item, index) => (
                                <div
                                    key={`${item.location.lon}-${item.location.lat}-${index}`}
                                    role="option"
                                    aria-selected={highlightIndex === index}
                                    className={cn(
                                        "flex cursor-pointer items-start gap-2 px-4 py-3 transition-colors",
                                        highlightIndex === index ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                                    )}
                                    onMouseEnter={() => setHighlightIndex(index)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => handleSelect(item)}
                                >
                                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                    <div className="min-w-0 pw-address-option" >
                                        <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
                                        <p className="truncate text-xs text-muted-foreground">{item.subtitle}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : null}
            </div>
        )
    },
)

AddressAutocomplete2.displayName = "AddressAutocomplete2"